import { motion } from "framer-motion";
import { Camera } from "lucide-react";

/**
 * <PhotoGallery /> — placeholder editorial gallery. Once the client uploads
 * gallery images, add them to /app/frontend/src/data/site.js as PHOTO_GALLERY
 * and this section will auto-render them in a luxurious 3-column masonry.
 */
export default function PhotoGallery({ images = [] }) {
  return (
    <section
      id="photo-gallery"
      className="relative bg-[var(--pam-forest)] text-white"
      style={{ padding: "5% 0" }}
      data-testid="photo-gallery-section"
    >
      <div className="container-pam">
        <div className="max-w-3xl">
          <p className="overline text-[var(--pam-gold)]">Gallery</p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.2, 0.9, 0.2, 1] }}
            className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Moments from
            <span className="italic text-[var(--pam-gold)]"> the Ghats.</span>
          </motion.h2>
        </div>

        {images.length > 0 ? (
          <>
            {/* Desktop: editorial masonry */}
            <div className="mt-14 hidden md:block columns-1 sm:columns-2 lg:columns-3 gap-5 [&>*]:mb-5">
              {images.map((img, i) => (
                <motion.figure
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: (i % 6) * 0.05 }}
                  className="break-inside-avoid rounded-2xl overflow-hidden ring-1 ring-white/10"
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt || "Peak Adventures Malnad gallery"}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-[900ms] hover:scale-105"
                  />
                </motion.figure>
              ))}
            </div>

            {/* Mobile: horizontal auto-scrolling image marquee (left → right) */}
            <div className="mt-10 md:hidden pam-marquee-wrap" data-testid="gallery-marquee-mobile">
              <div className="pam-marquee-track" style={{ animationDuration: "22s" }}>
                {[...images, ...images].map((img, i) => (
                  <figure
                    key={i}
                    className="shrink-0 w-[78vw] max-w-[320px] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-2xl"
                    data-testid={i < images.length ? `gallery-marquee-item-${i}` : undefined}
                  >
                    <img
                      src={img.src}
                      alt={img.alt || "Peak Adventures Malnad gallery"}
                      loading="lazy"
                      className="w-full h-56 object-cover"
                    />
                  </figure>
                ))}
              </div>
            </div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="mt-14 rounded-3xl border border-dashed border-white/15 bg-white/[0.03] backdrop-blur-md p-14 md:p-24 text-center"
            data-testid="gallery-empty-state"
          >
            <div className="w-14 h-14 rounded-full bg-[var(--pam-gold)]/15 border border-[var(--pam-gold)]/40 grid place-items-center mx-auto text-[var(--pam-gold)]">
              <Camera className="w-5 h-5" />
            </div>
            <p className="mt-6 text-white/70 max-w-lg mx-auto text-base sm:text-lg leading-relaxed">
              A curated editorial gallery from partner homestays, coffee
              estates, waterfalls and bonfire evenings will appear here soon.
            </p>
            <p className="mt-3 text-white/40 text-xs uppercase tracking-[0.32em]">
              Photography coming soon
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
